// Raccolta dei Web Vitals (opt-in `trackWebVitals`) sopra `web-vitals`, la
// libreria di riferimento di Google (CYJS-28).
//
// È l'UNICA dipendenza del pacchetto, ed è presa di proposito. LCP e CLS a mano
// sono fattibili, l'INP no: dipende dal percentile sulle interazioni, dal
// conteggio che il browser espone solo in parte e da una manciata di eccezioni
// che cambiano da una versione di Chrome all'altra. Una versione sbagliata non
// si vede — produce numeri plausibili — e resterebbe sbagliata per mesi. È anche
// l'unico modo per essere confrontabili con CrUX, che è tutto il punto di
// affiancare le due colonne nella stessa pagina.
//
// Import per-metrica (`web-vitals/onLCP.js`, non il barrel): l'IIFE cresce per
// tutti quelli che caricano lo snippet, anche per chi i Web Vitals non li
// accende. Il tetto è committato e la CI lo verifica (`pnpm size`).
//
// SOLO la build standard, MAI `web-vitals/attribution`: quella porta con sé
// l'elemento colpevole e pesa molto di più, e finché il backend non ha dove
// metterlo sarebbe peso pagato da ogni visitatore per un dato che nessuno legge.
//
// Comportamento di DEFAULT, non `reportAllChanges`: ogni metrica arriva una
// volta sola, col valore definitivo. Con `reportAllChanges` arriverebbe a ogni
// variazione, e sarebbe una richiesta per aggiornamento invece di una per
// caricamento.
//
// L'integrazione va installata DOPO il lifecycle del facade: da pagina in uscita
// l'invio deve passare dal beacon (CYJS-29), e il segnale lo alza il lifecycle.

import type { Metric } from 'web-vitals'
import { onCLS } from 'web-vitals/onCLS.js'
import { onFCP } from 'web-vitals/onFCP.js'
import { onINP } from 'web-vitals/onINP.js'
import { onLCP } from 'web-vitals/onLCP.js'
import { onTTFB } from 'web-vitals/onTTFB.js'
import type { Client } from '../../client'
import { buildWebVital } from '../../webVitals'
import type { WebVitalName, WebVitalPayload } from '../../webVitals'

// Intervallo del flush di cortesia: svuota il buffer dalle misure arrivate DOPO
// il primo invio (vedi `flushed` più sotto).
const COURTESY_FLUSH_MS = 5000

/// Dal vocabolario di web-vitals a quello dell'ingest.
const METRIC_NAMES: Record<Metric['name'], WebVitalName> = {
  LCP: 'lcp',
  CLS: 'cls',
  INP: 'inp',
  TTFB: 'ttfb',
  FCP: 'fcp',
}

// Destinatario corrente delle misure: l'installazione viva, o nessuno dopo lo
// smontaggio. È UNO perché di SDK inizializzato ce n'è uno.
let sink: ((metric: Metric) => void) | undefined
// I cinque callback sono stati registrati per questa pagina.
let listening = false

// Iscrive [onMetric] alle misure e ritorna la funzione per disiscriverlo.
//
// La registrazione presso `web-vitals` avviene UNA SOLA VOLTA per pagina, non a
// ogni installazione: la libreria non offre disiscrizione e ogni chiamata a
// `onLCP` & co. apre nuovi `PerformanceObserver` che restano vivi — e a lavorare
// sul main thread — fino alla fine della pagina. Registrarli a ogni `init` li
// accumulerebbe senza modo di chiuderli, e chi riconfigura l'SDK due volte
// pagherebbe il monitoraggio due volte. Peggio: la seconda registrazione fa
// riconsegnare le metriche già riportate, e lo stesso caricamento manderebbe due
// volte il suo LCP.
//
// Il prezzo, dichiarato: dopo un `close()` gli observer restano aperti, perché
// non esiste API per chiuderli. Quello che si può evitare è che si moltiplichino.
function subscribeToWebVitals(onMetric: (metric: Metric) => void): () => void {
  sink = onMetric
  if (!listening) {
    listening = true
    // Il dispatch è stabile: inoltra al destinatario del momento, così una
    // `init` successiva eredita gli observer già aperti invece di aprirne altri.
    const dispatch = (metric: Metric): void => sink?.(metric)
    onTTFB(dispatch)
    onFCP(dispatch)
    onLCP(dispatch)
    onCLS(dispatch)
    onINP(dispatch)
  }

  return () => {
    // Solo se il posto è ancora nostro: un'`init` successiva lo ha già preso, e
    // liberarlo qui spegnerebbe la raccolta di un client vivo.
    if (sink === onMetric) sink = undefined
  }
}

/// Registra i cinque callback e restituisce la funzione di disinstallazione, che
/// stacca i listener e manda comunque le misure raccolte fin lì: uno spegnimento
/// esplicito dell'SDK non è un buon motivo per buttare una misura già presa.
export function installWebVitals(client: Client): () => void {
  // Le misure aspettano qui l'uscita di pagina. Indicizzate per metrica perché
  // web-vitals può riportare la stessa più volte (il CLS cresce a ogni ritorno
  // in primo piano): finché il batch non è partito vale l'ultimo valore, che è
  // il più completo.
  const pending = new Map<WebVitalName, WebVitalPayload>()
  // Metriche già partite: una seconda misura della stessa metrica, nello stesso
  // caricamento, non si manda. Due valori della stessa pagina dentro un
  // percentile lo falsano più di un valore fermo al primo invio.
  //
  // Ci ricade anche il RITORNO DA BFCACHE, ed è voluto: web-vitals lo tratta
  // come una navigazione nuova e rimisura tutto, ma quelle misure qui restano
  // fuori. È la stessa scelta di CYJS-12, dichiarata nel README — dopo l'uscita
  // di pagina il caricamento è chiuso. Rimisurarlo è una decisione di prodotto
  // (raddoppia gli invii e apre la domanda di quale sia il "caricamento" per il
  // percentile), non un dettaglio da cambiare qui: il tipo di navigazione
  // `back-forward-cache` non è nemmeno nel vocabolario dell'ingest.
  const sent = new Set<WebVitalName>()
  // Pagina a cui le misure appartengono, letta all'installazione: in una SPA il
  // percorso può cambiare prima dell'uscita, e attribuire il caricamento
  // all'ultima route vista darebbe la colpa alla pagina sbagliata.
  const path = typeof location !== 'undefined' ? location.pathname : undefined

  // Il primo batch è partito. Prima di quel momento le misure aspettano
  // l'uscita di pagina, così un caricamento produce UNA richiesta; dopo, ogni
  // misura che arriva è già definitiva e non c'è un secondo `pagehide` su cui
  // contare — la porta via il flush di cortesia.
  let flushed = false
  let courtesy: ReturnType<typeof setTimeout> | undefined

  const scheduleCourtesyFlush = (): void => {
    if (courtesy !== undefined) return
    courtesy = setTimeout(() => {
      courtesy = undefined
      flush()
    }, COURTESY_FLUSH_MS)
  }

  const record = (metric: Metric): void => {
    const name = METRIC_NAMES[metric.name]
    if (!name || sent.has(name)) return

    pending.set(
      name,
      buildWebVital({
        metric: name,
        value: metric.value,
        path,
        navigationType: metric.navigationType,
      }),
    )
    if (flushed) scheduleCourtesyFlush()
  }

  const flush = (): void => {
    if (courtesy !== undefined) {
      clearTimeout(courtesy)
      courtesy = undefined
    }
    flushed = true
    if (pending.size === 0) return

    const measures = [...pending.values()]
    for (const name of pending.keys()) sent.add(name)
    pending.clear()
    client.captureWebVitals(measures)
  }

  const onVisibility = (): void => {
    if (typeof document === 'undefined') return
    if (document.visibilityState === 'hidden') flush()
  }

  // Prima l'iscrizione, poi i nostri listener: `web-vitals` consegna le misure
  // di LCP, CLS e INP proprio sul `visibilitychange`, e deve averlo già fatto
  // quando tocca a noi svuotare il buffer. Altrimenti servirebbe un secondo
  // invio per le stesse metriche. (I suoi listener sono in fase di capture su
  // `window`, i nostri in bubble su `document`: l'ordine regge anche quando
  // l'iscrizione riusa una registrazione fatta da un'installazione precedente.)
  const unsubscribe = subscribeToWebVitals(record)

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flush)
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility)
  }

  return () => {
    // La disiscrizione chiude la porta: una misura in ritardo non finisce in un
    // client già spento. Gli observer aperti da `web-vitals` restano — non c'è
    // API per chiuderli — ma sono UN set per pagina, non uno per `init`, e
    // un'installazione successiva eredita quelli invece di aprirne altri.
    //
    // Uno spegnimento a pagina ancora visibile manda ciò che è stato raccolto
    // fin qui, e non di più: LCP, CLS e INP li consegna la libreria all'uscita
    // di pagina, quindi a pagina viva possono non essere ancora arrivati. Non
    // c'è modo di farseli dare prima — l'unica leva sarebbe simulare un
    // `visibilitychange`, che mentirebbe a tutta la pagina, non solo a noi. Sono
    // valori ancora provvisori: perderli è meno peggio che inventarli.
    unsubscribe()
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', flush)
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility)
    }
    flush()
  }
}
